import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box, Typography, Paper, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SetupTab from '../components/adStrategist/SetupTab';
import CreationTab from '../components/adStrategist/CreationTab';
import OptimizationTab from '../components/adStrategist/OptimizationTab';
import DistributionTab from '../components/adStrategist/DistributionTab';
import { AdStrategyResponse } from '../services/adStrategistService';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ad-strategist-tabpanel-${index}`}
      aria-labelledby={`ad-strategist-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `ad-strategist-tab-${index}`,
    'aria-controls': `ad-strategist-tabpanel-${index}`,
  };
}

const AdStrategist: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [currentStrategy, setCurrentStrategy] = useState<AdStrategyResponse | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Check authentication when component mounts
  useEffect(() => {
    const token = localStorage.getItem('token');
    console.log("Auth check - Token exists:", !!token, "Is authenticated:", isAuthenticated, "User type:", user?.user_type);
    
    if (!isAuthenticated) {
      setAuthError("You must be logged in to use the Ad Strategist feature");
    } else if (user && user.user_type !== 'advertiser' && user.user_type !== 'admin') {
      setAuthError("Only advertisers can access the Ad Strategist feature");
    } else {
      setAuthError(null);
    }
  }, [isAuthenticated, user]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // Prevent navigation to tabs that require a strategy if none exists
    if ((newValue > 0 && !currentStrategy && !isCreating) || 
        (newValue > 1 && !currentStrategy)) {
      return;
    }
    setTabValue(newValue);
  };

  const handleStrategyCreated = (strategy: AdStrategyResponse) => {
    setIsLoading(true);
    
    // Validate that the strategy has all required properties before proceeding
    if (strategy && strategy.ad_variants && Array.isArray(strategy.ad_variants)) {
      setCurrentStrategy(strategy);
      setIsCreating(false);
      // Automatically move to the Creation tab to show the results
      setTabValue(1);
    } else {
      console.error("Received invalid strategy data:", strategy);
      // Stay on setup tab if strategy data is invalid
    }
    
    setIsLoading(false);
  };

  const handleCreateNew = () => {
    setCurrentStrategy(null);
    setIsCreating(true);
    setTabValue(0); // Go to setup tab
  };

  const handleStrategySelected = (strategy: AdStrategyResponse) => {
    setIsLoading(true);
    
    try {
      // Validate that the strategy has all required properties
      if (!strategy) {
        console.error("Selected strategy is null or undefined");
        setIsLoading(false);
        return;
      }
      
      // Check if strategy has the required ad_variants property
      if (!strategy.ad_variants || !Array.isArray(strategy.ad_variants) || strategy.ad_variants.length === 0) {
        console.error("Selected strategy has invalid or missing ad_variants:", strategy);
        setIsLoading(false);
        return;
      }
      
      // Validate that strategy has the strategy property
      if (!strategy.strategy) {
        console.error("Selected strategy has missing strategy property:", strategy);
        setIsLoading(false);
        return;
      }
      
      setCurrentStrategy(strategy);
      setIsCreating(false);
      setTabValue(1); // Go to creation tab to view the strategy
    } catch (error) {
      console.error("Error processing selected strategy:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      {authError ? (
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{authError}</Alert>
          <Button variant="contained" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Ad Strategist
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Generate AI-powered ad campaigns with exclusive high-converting formats
            </Typography>
          </Box>
          
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="ad strategist tabs"
              sx={{ 
                '& .MuiTab-root': { 
                  fontWeight: 'medium',
                  fontSize: '0.9rem', 
                  py: 2 
                } 
              }}
            >
              <Tab label="Setup" {...a11yProps(0)} />
              <Tab 
                label="Creation" 
                {...a11yProps(1)} 
                disabled={!currentStrategy && !isCreating} 
              />
              <Tab 
                label="Optimization" 
                {...a11yProps(2)} 
                disabled={!currentStrategy} 
              />
              <Tab 
                label="Distribution" 
                {...a11yProps(3)} 
                disabled={!currentStrategy} 
              />
            </Tabs>
          </Box>
          
          <TabPanel value={tabValue} index={0}>
            <SetupTab 
              onStrategyCreated={handleStrategyCreated} 
              onStrategySelected={handleStrategySelected}
              isCreating={isCreating}
            />
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <CreationTab 
              strategy={currentStrategy} 
              onCreateNew={handleCreateNew}
            />
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <OptimizationTab strategy={currentStrategy} />
          </TabPanel>
          
          <TabPanel value={tabValue} index={3}>
            <DistributionTab strategy={currentStrategy} />
          </TabPanel>
        </Paper>
      )}
    </Box>
  );
};

export default AdStrategist; 